import {
  fetchClinicTicket,
  fetchTicketComments,
  handleUpdateSupportRequest,
} from '@/entities/support';
import { handleCreateTicketComment, handleMarkTicketCommentAsSeen } from '@/entities/clinic';
import { useUserStore } from '@/entities/user';
import { useLoadingStore } from '@/shared/model';
import {
  LOADING_TYPES,
  SUPPORT_REQUEST_ACTION,
  SUPPORT_REQUEST_ACTION_CONTENT,
} from '@/shared/config';
import { checkShowCommentIcon, auth, showMessageError, showMessageSuccess } from '@/shared/lib';
import { useSetState, useElementChange } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';

/**
 * State and actions of the support request detail page. Reads the supportId route param plus the current user and page-loading setter from the stores, loads detail and comments through fetchData whenever supportId changes, sets the document title, toggles the header login-banner class and scrolls the comment body to the bottom as comments arrive, and exposes the comment, email and re-open handlers.
 *
 * @returns Page state, the body and header refs and the comment, email and re-open handlers.
 */
export const useSupportRequestDetails = () => {
  const { supportId } = useParams();

  /**
   * Currently signed-in user of the support request detail page. Read from the user store through a selector.
   *
   * @returns The current user record held in the store.
   */
  const meRedux = useUserStore((s) => s.me);

  /**
   * Setter for the global page loading text. Bound from the loading store action, and called without an argument to clear the loading overlay.
   *
   * @param loadingText - Loading text to display, omitted to clear it.
   * @returns Nothing.
   */
  const setLoadingPage = useLoadingStore((s) => s.setLoadingText);

  const bodyLeft = useRef<any>(null);
  const headerRef = useRef<any>(null);

  const isChangePosition = useElementChange(headerRef);

  const [state, setState] = useSetState<any>({
    isLoadedData: true,
    detailData: {},
    commentData: [],
  });

  /**
   * Loads a support request together with its comment thread. Fetches the ascending clinic ticket comments and the clinic ticket in parallel into state, then marks the thread as seen when checkShowCommentIcon reports an unseen last comment, logging failures and surfacing them through an error toast.
   *
   * @param supportId - Ticket id taken from the route parameter.
   * @returns A promise resolving once detail and comment state are set.
   */
  const fetchData = async (supportId: any) => {
    try {
      setState({ isLoadedData: false });
      const pending = [
        fetchTicketComments(
          {
            ticketId: supportId,
            sortOrder: 'asc',
            ticketType: 'Clinic',
          },
          0,
        ),
        fetchClinicTicket(supportId),
      ] as const;

      const [commentData, detailData] = await Promise.all(pending);
      setState({
        detailData,
        commentData,
        isLoadedData: true,
      });
      if (!_.isEmpty(detailData)) {
        const { id, lastSeenTicketComment } = detailData;
        const hasComment = checkShowCommentIcon(lastSeenTicketComment?.createdBy?.id);
        if (hasComment) {
          const input = {
            ticketId: id as string,
            ticketType: 'Clinic',
          };
          await handleMarkTicketCommentAsSeen(input);
        }
      }
    } catch (error) {
      console.error('Failed to fetch support request details: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({ isLoadedData: true });
    }
  };

  /**
   * Posts a new comment on the current support request. Appends the created clinic ticket comment to state.commentData and, when the signed-in user is absent from detailData.participants, adds their id and name to that list, reporting failures through an error toast.
   *
   * @param comment - Comment text to post.
   * @returns A promise resolving once the comment state was updated.
   */
  const handleUpdateComment = async (comment: any) => {
    if (!supportId) return;
    try {
      const newComment = await handleCreateTicketComment({
        ticketId: supportId,
        ticketType: 'Clinic',
        content: comment,
      });
      const commentDataTemp = [...state.commentData];
      commentDataTemp.push(newComment);

      const objectState = {
        commentData: commentDataTemp,
      };

      // check current user have been included participants
      // --> Don't included --> add manual current user into  participants
      const { id, firstName, lastName } = meRedux;
      /**
       * Whether the signed-in user already appears in the support request's participant list. Tests state.detailData.participants for an entry whose id equals the current user's id.
       *
       * @returns True when the current user is already a participant.
       */
      const isIncludedParticipants = _.some(
        state.detailData?.participants,
        (item) => item.id === id,
      );

      if (!isIncludedParticipants) {
        const detailDataTemp = { ...state.detailData };
        const userInfo = {
          id,
          firstName,
          lastName,
        };
        if (_.isArray(detailDataTemp.participants)) {
          detailDataTemp.participants.push(userInfo);
        } else {
          // case participants null or empty
          detailDataTemp.participants = [userInfo];
        }
        _.assign({ detailData: detailDataTemp });
      }

      setState(objectState);
    } catch (error) {
      console.error('Failed to update comments: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Saves the notification email list of the current support request. Sends the value as replyTo through handleUpdateSupportRequest, replaces detailData with the response and shows a success toast, or logs the failure and surfaces it as an error toast.
   *
   * @param comment - Notification email list to store as replyTo.
   * @returns A promise resolving once the ticket was updated.
   */
  const handleUpdateEmail = async (comment: any) => {
    try {
      const detailData = await handleUpdateSupportRequest({
        id: supportId as string,
        replyTo: comment,
      });
      setState({
        detailData,
      });
      showMessageSuccess({
        content: t`Update successfully!`,
      });
    } catch (error) {
      console.error('Failed to update email: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Re-opens the current support request. Shows the page loading state, sends isResolved false through handleUpdateSupportRequest, appends a locally built REOPEN comment authored by the signed-in user, then reports success or failure and clears the loading state.
   *
   * @returns A promise resolving once the ticket and comment state were refreshed.
   */
  const handleReOpen = async () => {
    try {
      setLoadingPage(LOADING_TYPES.LOADING);
      const currentTime = dayjs();
      const detailData = await handleUpdateSupportRequest({
        id: supportId as string,
        isResolved: false,
      });

      // Add new comment manual
      const commentDataTemp = [...state.commentData];
      const { firstName, lastName } = meRedux || {};
      commentDataTemp.push({
        createdBy: {
          firstName,
          lastName,
        },
        createdAt: currentTime,
        action: SUPPORT_REQUEST_ACTION.REOPEN,
        content: SUPPORT_REQUEST_ACTION_CONTENT.REOPEN,
      });

      setState({
        detailData,
        commentData: commentDataTemp,
      });
      showMessageSuccess({
        content: t`Update successfully!`,
      });
    } catch (error) {
      console.error('Failed to reopen support request: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setLoadingPage();
    }
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Support Request Details`;
  }, []);

  useEffect(() => {
    fetchData(supportId);
  }, [supportId]);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useEffect(() => {
    if (state.isLoadedData && bodyLeft.current) {
      const element = bodyLeft.current;
      element.scrollTo(0, element.scrollHeight);
    }
  }, [state.isLoadedData, bodyLeft.current, state.commentData]);

  return {
    state,
    bodyLeft,
    headerRef,
    handleUpdateComment,
    handleUpdateEmail,
    handleReOpen,
  };
};
